import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { getDocument, queryDocuments } from '../../services/dbService';
import { updateAppStatus } from '../../services/appStatusService';
import { AppStatus } from '../../constants/appStatus';
import { doc, getDoc, collection, query, where, getDocs, onSnapshot, arrayUnion } from 'firebase/firestore';
import { db } from '../../config/firebase';

/**
 * Logic hook for the app details page
 * Handles loading application data, analysis results and status updates
 */
const useAppDetailsLogic = () => {
  const { fileId } = useParams();
  const [appDetails, setAppDetails] = useState(null);
  const [analysisResults, setAnalysisResults] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Listen to apkAnalysisUIProgress and auto-update userApps status when analysis completes
  useEffect(() => {
    if (!fileId || !appDetails?.fileId) return;
    
    // Only listen if the app is still in analyzing_apk status
    if (appDetails.statusApp !== AppStatus.ANALYZING_APK) {
      console.log(`[AppDetailsLogic] App status is "${appDetails.statusApp}", not ANALYZING_APK - skipping analysis listener`);
      return;
    }

    console.log(`[AppDetailsLogic] Setting up analysis progress listener for fileId: ${appDetails.fileId}`);
    
    const analysisRef = doc(db, 'apkAnalysisUIProgress', appDetails.fileId);
    const unsubscribe = onSnapshot(analysisRef, async (docSnap) => {
      if (docSnap.exists()) {
        const analysisData = docSnap.data();
        console.log(`[AppDetailsLogic] Analysis progress update: status=${analysisData.status}, progress=${analysisData.progress}%`);

        // If analysis completed, auto-update userApps status
        if (analysisData.status === 'completed' && analysisData.progress === 100) {
          console.log(`[AppDetailsLogic] ✅ Analysis completed! Updating userApps status to deployment_started`);
          try {
            // Find the userApps document for this fileId (try direct match first, then versionFileIds)
            let q = query(collection(db, 'userApps'), where('fileId', '==', appDetails.fileId));
            let querySnapshot = await getDocs(q);
            
            if (querySnapshot.empty) {
              // Try versionFileIds for versioned apps
              q = query(collection(db, 'userApps'), where('versionFileIds', 'array-contains', appDetails.fileId));
              querySnapshot = await getDocs(q);
            }
            
            if (!querySnapshot.empty) {
              const userAppDoc = querySnapshot.docs[0];
              const currentStatus = userAppDoc.data().statusApp;
              
              if (currentStatus === AppStatus.ANALYZING_APK) {
                await updateAppStatus(userAppDoc.id, AppStatus.DEPLOYMENT_STARTED, 'Analysis completed successfully');
                console.log(`[AppDetailsLogic] ✅ userApps status updated to deployment_started`);
              } else {
                console.log(`[AppDetailsLogic] userApps status already: "${currentStatus}"`);
              }
            }
          } catch (updateError) {
            console.error(`[AppDetailsLogic] ❌ Error auto-updating status:`, updateError);
          }
        } else if (analysisData.status === 'error') {
          console.error(`[AppDetailsLogic] ❌ Analysis failed:`, analysisData.error);
        }
      } else {
        console.log(`[AppDetailsLogic] No analysis document found for fileId: ${appDetails.fileId}`);
      }
    });

    return () => unsubscribe();
  }, [fileId, appDetails?.fileId, appDetails?.statusApp]);

  // Load application data
  useEffect(() => {
    if (!fileId) {
      setError('Missing file identifier');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    console.log('Setting up real-time listener for app details with fileId:', fileId);

    // Listener for direct document by ID
    const docRef = doc(db, 'userApps', fileId);
    const unsubscribeAppDetailsDirect = onSnapshot(docRef, (docSnap) => {
      if (docSnap.exists()) {
        console.log('Real-time update: App details found directly:', docSnap.data());
        setAppDetails(docSnap.data());
        // Assuming successful direct load, fetch analysis results
        fetchAnalysisResults(fileId); 
        setIsLoading(false);
      } else {
        // If not found directly, try querying by fileId field
        console.log('Document not found directly, trying real-time query by fileId field');
        const q = query(collection(db, 'userApps'), where('fileId', '==', fileId));
        const unsubscribeAppDetailsQuery = onSnapshot(q, async (querySnapshot) => {
          if (!querySnapshot.empty) {
            const appDoc = querySnapshot.docs[0].data();
            console.log('Real-time update: Found document by fileId field query:', appDoc);
            setAppDetails(appDoc);
            fetchAnalysisResults(fileId);
            setIsLoading(false);
          } else {
            // Also try searching by versionFileIds (for versioned apps)
            console.log('Not found by fileId, trying versionFileIds array-contains...');
            const versionQuery = query(collection(db, 'userApps'), where('versionFileIds', 'array-contains', fileId));
            const versionSnapshot = await getDocs(versionQuery);
            
            if (!versionSnapshot.empty) {
              const appDoc = versionSnapshot.docs[0].data();
              console.log('Found document by versionFileIds:', appDoc);
              setAppDetails(appDoc);
              fetchAnalysisResults(fileId);
              setIsLoading(false);
            } else {
              console.error('Application not found for fileId:', fileId);
              setError('Application not found');
              setAppDetails(null);
              setIsLoading(false);
            }
          }
        }, (err) => {
          console.error('Error in real-time query for app details:', err);
          setError('Error loading application details');
          setIsLoading(false);
        });
        // Return this unsubscribe function if the direct one wasn't triggered
        return () => {
            console.log("Unsubscribing from app details query listener for fileId:", fileId);
            unsubscribeAppDetailsQuery();
        }
      }
    }, (err) => {
      console.error('Error in real-time listener for app details (direct):', err);
      setError('Error loading application details');
      setIsLoading(false);
    });

    // Fetch analysis results initially as well, outside the app details snapshot,
    // in case app details load but analysis needs its own lifecycle.
    // However, fetchAnalysisResults itself will be real-time.
    const unsubscribeAnalysis = fetchAnalysisResults(fileId);

    return () => {
      console.log("Unsubscribing from app details direct listener for fileId:", fileId);
      unsubscribeAppDetailsDirect();
      // If unsubscribeAnalysis is a function (meaning a listener was set up), call it
      if (typeof unsubscribeAnalysis === 'function') {
        unsubscribeAnalysis();
      }
    };
  }, [fileId]);

  // Keep track of the current analysis listener to unsubscribe
  let currentAnalysisUnsubscribe = null;

  // Fetch analysis results from apk_analysis collection using onSnapshot
  const fetchAnalysisResults = (currentFileId) => {
    // If there's an existing listener, unsubscribe from it first
    if (currentAnalysisUnsubscribe) {
      console.log("Unsubscribing from previous analysis listener.");
      currentAnalysisUnsubscribe();
      currentAnalysisUnsubscribe = null;
    }

    console.log('Setting up real-time listener for analysis results with fileId:', currentFileId);

    // Attempt 1: Direct lookup by currentFileId
    const apkAnalysisRef = doc(db, 'apk_analysis', currentFileId);
    const unsubscribeDirect = onSnapshot(apkAnalysisRef, (docSnap) => {
      if (docSnap.exists()) {
        const analysisData = docSnap.data();
        console.log('Real-time update: Found analysis document directly by ID', analysisData);
        setAnalysisResults(analysisData);
        // If we found it directly, we don't need other listeners for this fileId
        if (currentAnalysisUnsubscribe && currentAnalysisUnsubscribe !== unsubscribeDirect) {
            currentAnalysisUnsubscribe(); // unsubscribe from any alternative listener
        }
        currentAnalysisUnsubscribe = unsubscribeDirect; // store this as the active listener
        return; // Exit, no need to try other methods
      }

      console.log('Analysis document not found by direct ID, trying alternatives with real-time listeners...');
      
      // Attempt 2: Query by file_id field
      const filesCollection = collection(db, 'apk_analysis');
      const qFileId = query(filesCollection, where('file_id', '==', currentFileId));
      const unsubscribeFileIdQuery = onSnapshot(qFileId, (querySnapshot) => {
        if (!querySnapshot.empty) {
          const analysisData = querySnapshot.docs[0].data();
          console.log(`Real-time update: Found analysis by file_id field match: ${querySnapshot.docs[0].id}`, analysisData);
          setAnalysisResults(analysisData);
          if (currentAnalysisUnsubscribe && currentAnalysisUnsubscribe !== unsubscribeFileIdQuery) {
            currentAnalysisUnsubscribe();
          }
          currentAnalysisUnsubscribe = unsubscribeFileIdQuery;
          return;
        }

        // Attempt 3: Query by file_name field
        // Try both .aab and .apk extensions for backwards compatibility
        const qFileName = query(filesCollection, where('file_name', '==', `${currentFileId}.aab`));
        const unsubscribeFileNameQuery = onSnapshot(qFileName, (querySnapshotFileName) => {
          if (!querySnapshotFileName.empty) {
            const analysisData = querySnapshotFileName.docs[0].data();
            console.log(`Real-time update: Found analysis by file_name field match: ${querySnapshotFileName.docs[0].id}`, analysisData);
            setAnalysisResults(analysisData);
            if (currentAnalysisUnsubscribe && currentAnalysisUnsubscribe !== unsubscribeFileNameQuery) {
                currentAnalysisUnsubscribe();
            }
            currentAnalysisUnsubscribe = unsubscribeFileNameQuery;
            return;
          }

          // Attempt 4: Partial search if fileId is part of a longer identifier
          try {
            const fileBaseName = currentFileId.split('_')[0] + '_' + currentFileId.split('_')[1];
            const qPartial = query(filesCollection, where('file_id', '>=', fileBaseName), where('file_id', '<=', fileBaseName + '\uf8ff'));
            const unsubscribePartialQuery = onSnapshot(qPartial, (querySnapshotPartial) => {
              if (!querySnapshotPartial.empty) {
                const analysisData = querySnapshotPartial.docs[0].data();
                console.log(`Real-time update: Found analysis by partial file_id match: ${querySnapshotPartial.docs[0].id}`, analysisData);
                setAnalysisResults(analysisData);
                if (currentAnalysisUnsubscribe && currentAnalysisUnsubscribe !== unsubscribePartialQuery) {
                    currentAnalysisUnsubscribe();
                }
                currentAnalysisUnsubscribe = unsubscribePartialQuery;
                return;
              }
              console.log('No analysis results found in apk_analysis collection after all real-time attempts');
              setAnalysisResults(null); // Clear previous results if none found
            }, (err) => {
              console.log("Error in real-time partial search:", err);
              setAnalysisResults(null);
            });
            // This becomes the "fall-through" listener if others don't match
            if (currentAnalysisUnsubscribe && currentAnalysisUnsubscribe !== unsubscribePartialQuery) {
                currentAnalysisUnsubscribe();
            }
            currentAnalysisUnsubscribe = unsubscribePartialQuery;

          } catch (error) {
            console.log("Error setting up real-time partial search:", error);
            setAnalysisResults(null);
          }
        }, (err) => {
          console.log("Error in real-time file_name query:", err);
          setAnalysisResults(null);
        });
         if (currentAnalysisUnsubscribe && currentAnalysisUnsubscribe !== unsubscribeFileNameQuery) {
            currentAnalysisUnsubscribe();
         }
        currentAnalysisUnsubscribe = unsubscribeFileNameQuery;


      }, (err) => {
        console.log("Error in real-time file_id query:", err);
        setAnalysisResults(null);
      });
       if (currentAnalysisUnsubscribe && currentAnalysisUnsubscribe !== unsubscribeFileIdQuery) {
        currentAnalysisUnsubscribe();
       }
      currentAnalysisUnsubscribe = unsubscribeFileIdQuery;

    }, (err) => {
      console.error('Error in real-time listener for analysis results (direct):', err);
      setAnalysisResults(null); // Clear results on error
    });
    
    // Return a function that can unsubscribe from the currently active listener
    // This function is returned by fetchAnalysisResults and used in the main useEffect cleanup
    return () => {
        if(currentAnalysisUnsubscribe) {
            console.log("Unsubscribing from active analysis listener for fileId:", currentFileId);
            currentAnalysisUnsubscribe();
        }
    };
  };
  
  // Update application status
  const updateStatus = async (newStatus) => {
    try {
      if (!fileId || !appDetails) return;
      
      await updateAppStatus(fileId, newStatus);
      
      // Update local state - Firebase listener will do this automatically
      // setAppDetails(prev => ({
      // ...prev,
      // statusApp: newStatus,
      // lastStatusUpdate: new Date()
      // }));
      
      return true;
    } catch (error) {
      console.error('Error updating app status:', error);
      return false;
    }
  };
  
  // Upload new version (infrastructure only - to be completed)
  const uploadNewVersion = () => {
    // Implementation for uploading a new version needed
    console.log('Upload new version functionality not implemented yet');
  };
  
  // Move to the next stage in the process, based on current status
  const moveToNextStage = async () => {
    if (!appDetails || !appDetails.statusApp) return;
    
    const currentStatus = appDetails.statusApp;
    let nextStatus;
    
    switch (currentStatus) {
      case AppStatus.AWAITING_UPLOAD:
        nextStatus = AppStatus.ANALYZING_APK;
        break;
      case AppStatus.ANALYZING_APK:
        nextStatus = AppStatus.DEPLOYMENT_STARTED;
        break;
      case AppStatus.DEPLOYMENT_STARTED:
        nextStatus = AppStatus.INTERNAL_TESTING;
        break;
      case AppStatus.INTERNAL_TESTING:
        nextStatus = AppStatus.CLOSED_TESTING;
        break;
      case AppStatus.CLOSED_TESTING:
        nextStatus = AppStatus.PRODUCTION_RELEASE;
        break;
      case AppStatus.PRODUCTION_RELEASE:
        nextStatus = AppStatus.APP_LIVE;
        break;
      default:
        return; // Already in final status or unknown
    }
    
    return await updateStatus(nextStatus);
  };
  
  // Calculate permission data for pie chart
  const calculatePermissionsData = () => {
    if (!analysisResults || !analysisResults.report_summary || !analysisResults.report_summary.permissions) {
      return {
        safe: 0,
        warning: 0,
        dangerous: 0,
        total: 0,
        dangerousList: []
      };
    }
    
    // Get permissions from the report_summary structure
    const { permissions } = analysisResults.report_summary;
    let safe = 0;
    let warning = 0;
    let dangerous = 0;
    const dangerousList = [];
    
    // Count normal permissions as safe
    if (permissions.normal && Array.isArray(permissions.normal)) {
      safe = permissions.normal.length;
    }
    
    // Count dangerous permissions
    if (permissions.dangerous && Array.isArray(permissions.dangerous)) {
      dangerous = permissions.dangerous.length;
      // Add dangerous permissions to the list
      permissions.dangerous.forEach(perm => {
        dangerousList.push(perm.name);
      });
    }
    
    // Count unknown permissions as warnings
    if (permissions.unknown && Array.isArray(permissions.unknown)) {
      warning = permissions.unknown.length;
    }
    
    const total = safe + warning + dangerous;
    
    return {
      safe,
      warning,
      dangerous,
      total,
      dangerousList: dangerousList.slice(0, 5) // Return top 5 most dangerous permissions
    };
  };
  
  return {
    fileId,
    appDetails,
    analysisResults,
    isLoading,
    error,
    updateStatus,
    uploadNewVersion,
    moveToNextStage,
    permissionsData: calculatePermissionsData()
  };
};

export default useAppDetailsLogic; 